import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    DocumentTextIcon,
    ArrowDownTrayIcon,
    FunnelIcon,
    EyeIcon,
    PrinterIcon,
    ShareIcon
} from '@heroicons/react/24/outline';

interface Report {
    id: string;
    title: string;
    type: string;
    description: string;
    createdAt: string;
    status: 'completed' | 'generating' | 'failed';
    size: string;
    format: 'pdf' | 'excel' | 'csv';
}

const Reports: React.FC = () => {
    const dispatch = useAppDispatch();
    const [reports, setReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [reportType, setReportType] = useState('all');
    const [dateRange, setDateRange] = useState('30d');

    const fetchReports = async () => {
        try {
            setLoading(true);
            // For now, we'll create reports based on available data
            // In a real application, you might have a dedicated reports API
            const [usersResponse, companiesResponse, jobsResponse, paymentsResponse] = await Promise.all([
                api.get('/users'),
                api.get('/companies'),
                api.get('/jobs'),
                api.get('/payments')
            ]);

            const users = usersResponse.data.data || [];
            const companies = companiesResponse.data.data || [];
            const jobs = jobsResponse.data.data || [];
            const payments = paymentsResponse.data.data || [];

            // Generate reports based on available data
            const generatedReports: Report[] = [
                {
                    id: '1',
                    title: 'User Registration Report',
                    type: 'users',
                    description: `Total users: ${users.length}, Active: ${users.filter((u: any) => u.isActive).length}`,
                    createdAt: new Date().toISOString(),
                    status: 'completed',
                    size: '2.3 MB',
                    format: 'pdf'
                },
                {
                    id: '2',
                    title: 'Company Analytics Report',
                    type: 'companies',
                    description: `Total companies: ${companies.length}, Active: ${companies.filter((c: any) => c.isActive).length}`,
                    createdAt: new Date(Date.now() - 86400000).toISOString(),
                    status: 'completed',
                    size: '1.8 MB',
                    format: 'excel'
                },
                {
                    id: '3',
                    title: 'Job Posting Trends',
                    type: 'jobs',
                    description: `Total jobs: ${jobs.length}, Active: ${jobs.filter((j: any) => j.status === 'active').length}`,
                    createdAt: new Date(Date.now() - 172800000).toISOString(),
                    status: 'completed',
                    size: '3.1 MB',
                    format: 'pdf'
                },
                {
                    id: '4',
                    title: 'Revenue Report',
                    type: 'revenue',
                    description: `Total payments: ${payments.length}, Total amount: ₹${payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0)}`,
                    createdAt: new Date(Date.now() - 259200000).toISOString(),
                    status: 'completed',
                    size: '1.5 MB',
                    format: 'excel'
                }
            ];

            setReports(generatedReports);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch reports',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const generateReport = async (type: string) => {
        try {
            dispatch(showToast({
                message: 'Report generation started',
                type: 'info'
            }));

            // Mock report generation
            const newReport: Report = {
                id: Date.now().toString(),
                title: `${type.charAt(0).toUpperCase() + type.slice(1)} Report`,
                type: type,
                description: `Generated ${type} report`,
                createdAt: new Date().toISOString(),
                status: 'generating',
                size: '0 MB',
                format: 'pdf'
            };

            setReports([newReport, ...reports]);

            // Simulate report generation completion
            setTimeout(() => {
                setReports(prevReports =>
                    prevReports.map(report =>
                        report.id === newReport.id
                            ? { ...report, status: 'completed', size: '2.1 MB' }
                            : report
                    )
                );
                dispatch(showToast({
                    message: 'Report generated successfully',
                    type: 'success'
                }));
            }, 3000);

        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to generate report',
                type: 'error'
            }));
        }
    };

    const downloadReport = (reportId: string) => {
        dispatch(showToast({
            message: 'Download started',
            type: 'info'
        }));
        // Implement actual download logic
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'generating':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'failed':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getFormatBadgeColor = (format: string) => {
        switch (format) {
            case 'pdf':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'excel':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'csv':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const filteredReports = reports.filter(report => {
        return reportType === 'all' || report.type === reportType;
    });

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports & Analytics</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Generate and manage system reports
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors mr-3"
                        >
                            <FunnelIcon className="h-5 w-5 mr-2" />
                            Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Quick Report Generation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Generate New Report</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button
                        onClick={() => generateReport('users')}
                        className="flex items-center justify-center p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <DocumentTextIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">User Report</span>
                    </button>

                    <button
                        onClick={() => generateReport('companies')}
                        className="flex items-center justify-center p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <DocumentTextIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Company Report</span>
                    </button>

                    <button
                        onClick={() => generateReport('jobs')}
                        className="flex items-center justify-center p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <DocumentTextIcon className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Job Report</span>
                    </button>

                    <button
                        onClick={() => generateReport('revenue')}
                        className="flex items-center justify-center p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <DocumentTextIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Revenue Report</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Report Type
                            </label>
                            <select
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="all">All Types</option>
                                <option value="users">Users</option>
                                <option value="companies">Companies</option>
                                <option value="jobs">Jobs</option>
                                <option value="revenue">Revenue</option>
                                <option value="system">System</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Date Range
                            </label>
                            <select
                                value={dateRange}
                                onChange={(e) => setDateRange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="7d">Last 7 days</option>
                                <option value="30d">Last 30 days</option>
                                <option value="90d">Last 90 days</option>
                                <option value="1y">Last year</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Reports List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generated Reports</h3>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredReports.map((report) => (
                        <div key={report.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                        <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                                        <div>
                                            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                                                {report.title}
                                            </h4>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {report.description}
                                            </p>
                                            <div className="flex items-center space-x-4 mt-2">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(report.status)}`}>
                                                    {report.status}
                                                </span>
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFormatBadgeColor(report.format)}`}>
                                                    {report.format.toUpperCase()}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {report.size}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {new Date(report.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    {report.status === 'completed' && (
                                        <>
                                            <button
                                                onClick={() => downloadReport(report.id)}
                                                className="p-2 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition-colors"
                                                title="Download"
                                            >
                                                <ArrowDownTrayIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="View"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="Print"
                                            >
                                                <PrinterIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="Share"
                                            >
                                                <ShareIcon className="h-5 w-5" />
                                            </button>
                                        </>
                                    )}
                                    {report.status === 'generating' && (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                            <span className="text-sm text-gray-500 dark:text-gray-400">Generating...</span>
                                        </div>
                                    )}
                                    {report.status === 'failed' && (
                                        <button
                                            onClick={() => generateReport(report.type)}
                                            className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                        >
                                            Retry
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredReports.length === 0 && (
                    <div className="text-center py-12">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No reports found</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Generate your first report to get started.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reports;
